import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import {
  TrendingUp, Users, FileText, AlertCircle, GitBranch, Trophy, Wallet, Eye, UserCheck, FileSignature, CheckCircle2,
} from "lucide-react";

const STAGE_LABEL = {
  nuevo: "Nuevo",
  contactado: "Contactado",
  calificado: "Calificado",
  propuesta: "Propuesta",
  negociacion: "Negociación",
  ganado: "Ganado",
  perdido: "Perdido",
};
const STAGE_COLOR = {
  nuevo: "#9BA7B3",
  contactado: "#7BA6B0",
  calificado: "#5B8FAF",
  propuesta: "#D97757",
  negociacion: "#D9A557",
  ganado: "#1B7E3F",
  perdido: "#9B1C1C",
};

const fmtCLP = (v) => `$${Math.round(v || 0).toLocaleString("es-CL")}`;

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/admin/dashboard").then((r) => setStats(r.data)).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="p-6 text-ash-300">Cargando…</div>;
  if (!stats) return <div className="p-6 text-ash-300">Error al cargar.</div>;

  return (
    <div data-testid="admin-dashboard" className="p-4 md:p-6">
      <div className="mb-4">
        <div className="text-xs uppercase tracking-widest text-brand font-mono mb-1">Vista general</div>
        <h1 className="font-display font-bold text-2xl md:text-3xl text-ash-100 tracking-tight">Dashboard</h1>
      </div>

      {/* KPI cards — leads + cotizaciones */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-ink-500 border border-ink-500 mb-2">
        <KPI label="Leads totales" value={stats.total_leads} icon={Users} testid="kpi-total-leads" />
        <KPI label="Leads nuevos" value={stats.new_leads} icon={AlertCircle} accent testid="kpi-new-leads" />
        <KPI label="Solicitudes web" value={stats.total_quotes} icon={FileText} testid="kpi-total-quotes" />
        <KPI label="Nuevas" value={stats.new_quotes} icon={TrendingUp} accent testid="kpi-new-quotes" />
      </div>

      {/* KPI cards — propuestas */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-ink-500 border border-ink-500 mb-2">
        <KPI label="Propuestas" value={stats.total_proposals || 0} icon={FileSignature} testid="kpi-total-proposals" />
        <KPI label="Aceptadas" value={stats.accepted_proposals || 0} icon={CheckCircle2} accent testid="kpi-accepted-proposals" />
        <KPI label="Borradores" value={(stats.proposals_by_status && stats.proposals_by_status.draft) || 0} icon={FileSignature} testid="kpi-draft-proposals" />
        <KPI label="Enviadas" value={(stats.proposals_by_status && stats.proposals_by_status.sent) || 0} icon={FileSignature} accent testid="kpi-sent-proposals" />
      </div>

      {/* KPI cards — prospectos (CRM) */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-px bg-ink-500 border border-ink-500 mb-2">
        <KPI
          label="Prospectos"
          value={stats.total_prospects}
          icon={GitBranch}
          testid="kpi-total-prospects"
        />
        <KPI
          label="En pipeline"
          value={stats.open_prospects}
          icon={GitBranch}
          accent
          testid="kpi-open-prospects"
        />
        <KPI
          label="Ganados"
          value={stats.won_prospects}
          icon={Trophy}
          testid="kpi-won-prospects"
        />
      </div>

      {/* Pipeline + Ganado, separados por tipo de ingreso (único / mensual) */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-ink-500 border border-ink-500 mb-2">
        <KPI label="Pipeline (único)" value={fmtCLP(stats.pipeline_value)} icon={Wallet} mono testid="kpi-pipeline-value" />
        <KPI label="Pipeline (mensual)" value={`${fmtCLP(stats.pipeline_value_monthly || 0)}/m`} icon={Wallet} accent mono testid="kpi-pipeline-value-monthly" />
        <KPI label="Ganado (único)" value={fmtCLP(stats.won_value || 0)} icon={Trophy} mono testid="kpi-won-value" />
        <KPI label="Ganado (mensual)" value={`${fmtCLP(stats.won_value_monthly || 0)}/m`} icon={Trophy} accent mono testid="kpi-won-value-monthly" />
      </div>

      {/* KPI cards — analítica del sitio web */}
      {(stats.page_views_total > 0 || stats.unique_visitors_total > 0) && (
        <div className="mb-6">
          <div className="text-[11px] uppercase tracking-widest text-ash-500 font-mono mb-2">
            Tráfico del sitio web · últimos 30 días
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-ink-500 border border-ink-500">
            <KPI label="Vistas (30d)" value={(stats.page_views_30d || 0).toLocaleString("es-CL")} icon={Eye} accent testid="kpi-views-30d" />
            <KPI label="Únicos (30d)" value={(stats.unique_visitors_30d || 0).toLocaleString("es-CL")} icon={UserCheck} testid="kpi-unique-30d" />
            <KPI label="Vistas totales" value={(stats.page_views_total || 0).toLocaleString("es-CL")} icon={Eye} testid="kpi-views-total" />
            <KPI label="Únicos totales" value={(stats.unique_visitors_total || 0).toLocaleString("es-CL")} icon={UserCheck} accent testid="kpi-unique-total" />
          </div>
        </div>
      )}

      {/* Two-column grid: breakdowns left, recents right */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="space-y-4">
          <Panel title="Leads por industria">
            {Object.keys(stats.leads_by_industry).length === 0 ? (
              <div className="text-ash-500 text-sm">Sin datos aún.</div>
            ) : (
              <ul className="space-y-1.5">
                {Object.entries(stats.leads_by_industry).map(([k, v]) => (
                  <li key={k} className="flex items-center justify-between text-sm border-b border-ink-500 pb-1.5 last:border-0">
                    <span className="text-ash-100">{k}</span>
                    <span className="font-mono text-brand">{v}</span>
                  </li>
                ))}
              </ul>
            )}
          </Panel>
          <Panel title="Cotizaciones por estado">
            {Object.keys(stats.quotes_by_status).length === 0 ? (
              <div className="text-ash-500 text-sm">Sin datos aún.</div>
            ) : (
              <ul className="space-y-1.5">
                {Object.entries(stats.quotes_by_status).map(([k, v]) => (
                  <li key={k} className="flex items-center justify-between text-sm border-b border-ink-500 pb-1.5 last:border-0">
                    <span className="text-ash-100 capitalize">{k}</span>
                    <span className="font-mono text-brand">{v}</span>
                  </li>
                ))}
              </ul>
            )}
          </Panel>
          <Panel
            title="Prospectos por etapa"
            action={<Link to="/admin/crm" className="text-[10px] uppercase tracking-widest text-brand hover:underline">Ver CRM →</Link>}
          >
            {Object.keys(stats.prospects_by_stage || {}).length === 0 ? (
              <div className="text-ash-500 text-sm">Aún no hay prospectos.</div>
            ) : (
              <ul className="space-y-1.5">
                {Object.entries(stats.prospects_by_stage).map(([k, v]) => (
                  <li key={k} className="flex items-center justify-between text-sm border-b border-ink-500 pb-1.5 last:border-0">
                    <span className="flex items-center gap-2 text-ash-100">
                      <span className="w-2 h-2" style={{ background: STAGE_COLOR[k] || "#4A555C" }} />
                      {STAGE_LABEL[k] || k}
                    </span>
                    <span className="font-mono text-brand">{v}</span>
                  </li>
                ))}
              </ul>
            )}
          </Panel>
          {(stats.top_paths && stats.top_paths.length > 0) && (
            <Panel title="Páginas más visitadas (30d)">
              <ul className="space-y-1.5">
                {stats.top_paths.map((p) => {
                  const max = Math.max(...stats.top_paths.map((x) => x.views), 1);
                  const pct = Math.round((p.views / max) * 100);
                  return (
                    <li key={p.path} className="text-sm">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-ash-100 font-mono truncate max-w-[70%]">{p.path}</span>
                        <span className="font-mono text-brand">{p.views.toLocaleString("es-CL")}</span>
                      </div>
                      <div className="h-1 bg-ink-700">
                        <div className="h-1 bg-brand/60" style={{ width: `${pct}%` }} />
                      </div>
                    </li>
                  );
                })}
              </ul>
            </Panel>
          )}
        </div>

        <div className="space-y-4">
          <Panel title="Leads recientes">
            {stats.recent_leads.length === 0 ? (
              <div className="text-ash-500 text-sm">Aún no hay leads.</div>
            ) : (
              <ul className="space-y-2">
                {stats.recent_leads.slice(0, 3).map((l) => (
                  <li key={l.id} className="border-b border-ink-500 pb-2 last:border-0">
                    <div className="flex items-center justify-between">
                      <div className="text-ash-100 text-sm font-medium truncate">{l.name}</div>
                      <span className="text-[10px] uppercase tracking-widest text-brand font-mono shrink-0 ml-2">{l.status}</span>
                    </div>
                    <div className="text-xs text-ash-500 truncate">{l.email} · {l.company || "—"}</div>
                  </li>
                ))}
              </ul>
            )}
          </Panel>
          <Panel title="Cotizaciones recientes">
            {stats.recent_quotes.length === 0 ? (
              <div className="text-ash-500 text-sm">Aún no hay cotizaciones.</div>
            ) : (
              <ul className="space-y-2">
                {stats.recent_quotes.slice(0, 3).map((q) => (
                  <li key={q.id} className="border-b border-ink-500 pb-2 last:border-0">
                    <div className="flex items-center justify-between">
                      <div className="text-ash-100 text-sm font-medium truncate">{q.project_type}</div>
                      <span className="text-[10px] uppercase tracking-widest text-brand font-mono shrink-0 ml-2">{q.status}</span>
                    </div>
                    <div className="text-xs text-ash-500 truncate">{q.name} · {q.company || "—"}</div>
                  </li>
                ))}
              </ul>
            )}
          </Panel>
          <Panel
            title="Propuestas recientes"
            action={<Link to="/admin/proposals" className="text-[10px] uppercase tracking-widest text-brand hover:underline">Ver propuestas →</Link>}
          >
            {!stats.recent_proposals || stats.recent_proposals.length === 0 ? (
              <div className="text-ash-500 text-sm">Aún no hay propuestas.</div>
            ) : (
              <ul className="space-y-2">
                {stats.recent_proposals.slice(0, 4).map((p) => (
                  <li key={p.id} className="border-b border-ink-500 pb-2 last:border-0">
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <div className="text-ash-100 text-sm font-medium truncate">{p.client_name || "Sin cliente"}</div>
                        <div className="text-xs text-ash-500 truncate font-mono">{p.number} · {p.project_name || "—"}</div>
                      </div>
                      <span className="text-[10px] uppercase tracking-widest text-brand font-mono shrink-0">{p.status}</span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Panel>

          <Panel
            title="Prospectos recientes"
            action={<Link to="/admin/agenda" className="text-[10px] uppercase tracking-widest text-brand hover:underline">Ver agenda →</Link>}
          >
            {!stats.recent_prospects || stats.recent_prospects.length === 0 ? (
              <div className="text-ash-500 text-sm">Aún no hay prospectos.</div>
            ) : (
              <ul className="space-y-2">
                {stats.recent_prospects.slice(0, 4).map((p) => (
                  <li key={p.id} className="border-b border-ink-500 pb-2 last:border-0">
                    <div className="flex items-center justify-between gap-2">
                      <div className="text-ash-100 text-sm font-medium truncate">{p.name}</div>
                      <span
                        className="text-[9px] uppercase tracking-widest font-mono shrink-0 px-1.5 py-0.5 text-white"
                        style={{ backgroundColor: STAGE_COLOR[p.stage] || "#4A555C" }}
                      >
                        {STAGE_LABEL[p.stage] || p.stage}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-xs text-ash-500 truncate mt-0.5">
                      <span className="truncate">{p.company || "—"}{p.position ? ` · ${p.position}` : ""}</span>
                      {Number(p.value) > 0 && (
                        <span className="font-mono text-brand shrink-0 ml-2">{fmtCLP(p.value)}</span>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Panel>
        </div>
      </div>
    </div>
  );
}

function KPI({ label, value, icon: Icon, accent, mono, testid }) {
  return (
    <div data-testid={testid} className={`px-3 py-2.5 ${accent ? "bg-ink-700" : "bg-ink-800"} flex items-center gap-3`}>
      <Icon size={14} strokeWidth={1.5} className={`shrink-0 ${accent ? "text-brand" : "text-ash-300"}`} />
      <div className="min-w-0 flex-1">
        <div className={`font-display font-black text-ash-100 tracking-tighter leading-none truncate ${mono ? "text-lg" : "text-xl"}`}>{value}</div>
        <div className="text-[9px] uppercase tracking-widest text-ash-300 mt-1 font-mono truncate">{label}</div>
      </div>
    </div>
  );
}

function Panel({ title, children, action }) {
  return (
    <div className="bg-ink-800 border border-ink-500 p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-display font-semibold text-ash-100 text-sm uppercase tracking-widest">{title}</h3>
        {action}
      </div>
      {children}
    </div>
  );
}
